import type { RiderValue } from '@/pages/shipments/components/steps/Direct4WRiderStep';
import type { RoutePlanCostItem } from '@/services/api/routePlans';
import type {
  CreateUjpPayload,
  UjpClientRule,
  UjpDeliveryType,
  UjpDetail,
  UjpEMoney,
  UjpEnergyType,
  UjpEstimatePayload,
  UjpFuelType,
  UjpPayeeType,
  UjpSubconVendor,
  UjpVehicle,
} from '@/services/api/ujp';

import { UJP_COPY } from '../copy';
import { toNum, todayIso } from '../format';
import {
  emptyCostDrafts,
  foldedCostNote,
  toCostDrafts,
  toCostItemsPayload,
  type CostItemDraft,
} from './CostItemsEditor';
import {
  defaultKmProposed,
  emptyRouteDraft,
  endsInPoolDrop,
  fromSavedRoute,
  kmAllLegs,
  kmCharged,
  routeAnyPoolCharged,
  routeChargedOverride,
  routeDraftErrors,
  toRoutePayload,
  type RouteDraft,
} from './ujpWizard';

/**
 * The UJP wizard's form model: one plain object, pure validators per step and
 * the two payload builders. React-free on purpose — the risky parts (what is
 * required when the payee is a subcon, which fields the server is allowed to
 * see, how a draft becomes `POST /v1/ujp`) are unit-tested without a modal.
 *
 * Money is carried as DIGIT strings ("185000") and km / baseline as decimal
 * strings ("141.2"), matching both the inputs and the wire contract, so a
 * value never round-trips through a float.
 */

export type UjpStepIndex = 1 | 2 | 3 | 4 | 5;

/**
 * What the wizard keeps of the picked route plan for the Biaya step: its usual
 * costs (pre-fill), its note (shown under the picker) and who last changed it
 * (the "diperbarui … oleh …" line). PLAN-ROUTE-PLAN-COST-ITEMS.
 */
export interface PickedPlanInfo {
  name: string;
  costItems: RoutePlanCostItem[];
  /** The plan's fixed BBM amount (money string) or null — pre-fills Override BBM. */
  bbmFixOverride: string | null;
  notes: string | null;
  updatedAt: string | null;
  updatedBy: string | null;
}

export interface UjpFormState {
  // ── Info ──────────────────────────────────────────────────────────────────
  clientId: string;
  deliveryDate: string;
  serviceType: string;
  deliveryType: UjpDeliveryType;
  shift: string;
  /** Region for fuel-price resolution; '' = global default (R3). */
  region: string;
  isReverse: boolean;
  /** Set once the reverse switch has been touched, so the auto-tick stops. */
  reverseTouched: boolean;

  // ── Revenue tags (CR-4) ─────────────────────────────────────────────────────
  tariffConfigId: string;
  ringId: string;
  /** Set once ops touched the ring so the auto-suggest stops overriding. */
  ringTouched: boolean;

  // ── Rute ──────────────────────────────────────────────────────────────────
  /** The picked route plan (CR-2); routes are built in the Route Planner now. */
  routePlanId: string;
  /** Snapshot of the picked plan's stops/legs — powers the estimate + km. */
  route: RouteDraft;
  kmProposed: string;
  kmTouched: boolean;
  /** The picked plan's costs + note; null when no plan (or a manual route). */
  planInfo: PickedPlanInfo | null;

  // ── Biaya ─────────────────────────────────────────────────────────────────
  payee: UjpPayeeType;
  vendorId: string;
  nominalTransfer: string;
  plateNumber: string;
  unitType: string;
  vehicleId: string;
  energyType: UjpEnergyType;
  fuelType: UjpFuelType | '';
  baseline: string;
  konsumsiPerKm: string;
  baselineTouched: boolean;
  energyPrice: string;
  priceTouched: boolean;
  eMoney: UjpEMoney;
  /** Physical e-money card used for THIS trip — a per-UJP note. */
  eMoneyCardNo: string;
  overrideBbm: boolean;
  bbmFixOverride: string;
  /** Set once ops touched the Override BBM switch or nominal: a picked plan's
   *  override no longer overwrites it (ASSESSMENT-ROUTE-PLAN-BBM-OVERRIDE D3),
   *  only the "Terapkan biaya dari rute" link does. */
  bbmTouched: boolean;
  /** The six biaya operasional slots (CostItemsEditor); sent as name + amount
   *  rows (PLAN-UJP-DYNAMIC-COSTS), zero slots left out. */
  costItems: CostItemDraft[];
  /** Optional "Catatan biaya" for the approver (wire: justifikasiBiayaLainLain). */
  justifikasi: string;
  /** Set once ops edited a cost row: route-plan rows no longer overwrite the
   *  list (R1), only the "Terapkan biaya dari rute" link does. */
  costsTouched: boolean;
  /** The rows currently are the picked plan's rows. */
  costsFromPlan: boolean;

  // ── Driver ────────────────────────────────────────────────────────────────
  rider: RiderValue;
  vendorDriverName: string;
  bankName: string;
  accountNumber: string;
  accountHolder: string;
}

export const emptyRider = (): RiderValue => ({
  id: '',
  code: '',
  name: '',
  phone: '',
});

export const blankUjpForm = (now: Date = new Date()): UjpFormState => ({
  clientId: '',
  deliveryDate: todayIso(now),
  serviceType: '',
  deliveryType: 'SINGLE_DROP',
  shift: '',
  region: '',
  isReverse: false,
  reverseTouched: false,

  tariffConfigId: '',
  ringId: '',
  ringTouched: false,

  routePlanId: '',
  route: emptyRouteDraft(),
  kmProposed: '',
  kmTouched: false,
  planInfo: null,

  payee: 'driver',
  vendorId: '',
  nominalTransfer: '0',
  plateNumber: '',
  unitType: '',
  vehicleId: '',
  energyType: 'ICE',
  fuelType: '',
  baseline: '',
  konsumsiPerKm: '',
  baselineTouched: false,
  energyPrice: '',
  priceTouched: false,
  eMoney: 'NONE',
  eMoneyCardNo: '',
  overrideBbm: false,
  bbmFixOverride: '0',
  bbmTouched: false,
  costItems: emptyCostDrafts(),
  justifikasi: '',
  costsTouched: false,
  costsFromPlan: false,

  rider: emptyRider(),
  vendorDriverName: '',
  bankName: '',
  accountNumber: '',
  accountHolder: '',
});

// ─────────────────────────────────────────────────────────────────────────────
// Master prefills
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Picking a vehicle fills baseline / konsumsi and the energy + fuel type from
 * the master. Fields the operator has already typed over stay put — the
 * "diubah manual" tag is a promise that the master will not take them back.
 *
 * E-money defaults from the vehicle master (`defaultEMoney`) but only while the
 * operator has not already chosen one (still `NONE`); a truck with no default
 * leaves it at `NONE`. The e-money card number prefills the same way from the
 * truck's master value while the operator has not typed one.
 */
export const applyVehicle = (
  form: UjpFormState,
  vehicle: UjpVehicle
): UjpFormState => ({
  ...form,
  vehicleId: vehicle.id,
  plateNumber: vehicle.plateNumber,
  unitType: vehicle.unitType ?? form.unitType,
  energyType: vehicle.energyType,
  fuelType: vehicle.fuelType ?? '',
  baseline: form.baselineTouched
    ? form.baseline
    : vehicle.baseline != null
      ? String(vehicle.baseline)
      : '',
  konsumsiPerKm: form.baselineTouched
    ? form.konsumsiPerKm
    : vehicle.konsumsiPerKm != null
      ? String(vehicle.konsumsiPerKm)
      : '',
  eMoney:
    form.eMoney === 'NONE' && vehicle.defaultEMoney
      ? vehicle.defaultEMoney
      : form.eMoney,
  // Card number prefills from the truck's master value, but only while the
  // operator has not typed one — same promise as the fields above.
  eMoneyCardNo: form.eMoneyCardNo.trim()
    ? form.eMoneyCardNo
    : (vehicle.eMoneyCardNo ?? ''),
});

/** Picking a subcon vendor fills the payee bank block from the master (CR-D6). */
export const applyVendor = (
  form: UjpFormState,
  vendor: UjpSubconVendor
): UjpFormState => ({
  ...form,
  vendorId: vendor.id,
  bankName: vendor.bankName ?? '',
  accountNumber: vendor.accountNumber ?? '',
  accountHolder: vendor.accountHolder ?? '',
});

/**
 * A picked driver prefills the rekening from the driver master
 * (`GET /v3/drivers/:id` → `driver_bank`) — but only into an untouched bank
 * block, so a rekening ops already typed (or one loaded from an existing UJP in
 * edit mode) is never overwritten. Returns the same object when nothing
 * changes, so callers can `===` to know whether to show the hint.
 */
export interface RiderBankSource {
  bankName: string | null;
  accountNumber: string | null;
  accountHolder: string | null;
}

export const applyRiderBank = (
  form: UjpFormState,
  bank: RiderBankSource | null | undefined
): UjpFormState => {
  if (!bank) return form;
  const untouched =
    !form.bankName.trim() &&
    !form.accountNumber.trim() &&
    !form.accountHolder.trim();
  if (!untouched) return form;
  const next = {
    bankName: (bank.bankName ?? '').trim(),
    accountNumber: (bank.accountNumber ?? '').replace(/\D/g, ''),
    accountHolder: (bank.accountHolder ?? '').trim(),
  };
  if (!next.bankName && !next.accountNumber && !next.accountHolder) return form;
  return { ...form, ...next };
};

// ─────────────────────────────────────────────────────────────────────────────
// Route-plan costs (PLAN-ROUTE-PLAN-COST-ITEMS)
// ─────────────────────────────────────────────────────────────────────────────

/** Rows that only came from the plan (never edited) go away again. */
const dropUntouchedPlanCosts = (form: UjpFormState): UjpFormState =>
  form.costsFromPlan && !form.costsTouched
    ? { ...form, costItems: emptyCostDrafts(), costsFromPlan: false }
    : form;

/**
 * The plan's Override BBM onto the form (ASSESSMENT-ROUTE-PLAN-BBM-OVERRIDE,
 * D3): a plan that carries one turns the switch on with its nominal, a plan
 * without one leaves the switch alone, a subcon UJP never gets it (its switch
 * is disabled). `force` is the "Terapkan biaya dari rute" link; otherwise an
 * override ops already touched is kept.
 */
const applyPlanBbmOverride = (form: UjpFormState, force: boolean): UjpFormState => {
  const planBbm = form.planInfo?.bbmFixOverride ?? null;
  if (planBbm == null || form.payee === 'subcon') return form;
  if (form.bbmTouched && !force) return form;
  return {
    ...form,
    overrideBbm: true,
    bbmFixOverride: moneyDigits(planBbm),
    bbmTouched: force ? true : form.bbmTouched,
  };
};

/**
 * Bring the cost rows in line with the picked plan (R1, R3): a subcon UJP
 * never carries plan costs (its cost rows are disabled and unpaid), edited
 * rows are never overwritten, and untouched plan rows follow the plan. The
 * plan's rows fill the matching slots; a row named like no slot lands in
 * "Biaya lain-lain" and its name is added to the catatan biaya. The plan's
 * Override BBM rides along under the same touched rule.
 */
export const syncPlanCosts = (form: UjpFormState): UjpFormState =>
  applyPlanBbmOverride(syncPlanCostRows(form), false);

const syncPlanCostRows = (form: UjpFormState): UjpFormState => {
  const items = form.planInfo?.costItems ?? [];
  if (form.payee === 'subcon' || items.length === 0) {
    return dropUntouchedPlanCosts(form);
  }
  if (form.costsTouched) return form;
  return {
    ...form,
    costItems: toCostDrafts(items),
    justifikasi: foldedCostNote(form.justifikasi, items),
    costsFromPlan: true,
  };
};

/**
 * The "Terapkan biaya dari rute" link: put the plan's rows (and its Override
 * BBM) back over edited ones, on purpose. The catatan biaya is ops' own and
 * stays (only the names of rows folded into lain-lain are appended).
 */
export const applyPlanCostsNow = (form: UjpFormState): UjpFormState => {
  const items = form.planInfo?.costItems ?? [];
  if (form.payee === 'subcon') return form;
  const withBbm = applyPlanBbmOverride(form, true);
  if (items.length === 0) return withBbm;
  return {
    ...withBbm,
    costItems: toCostDrafts(items),
    justifikasi: foldedCostNote(form.justifikasi, items),
    costsFromPlan: true,
    costsTouched: true,
  };
};

/** The plan link shows only when typed values would hide the plan's costs or override. */
export const canApplyPlanCosts = (form: UjpFormState): boolean =>
  form.payee !== 'subcon' &&
  ((form.costsTouched && (form.planInfo?.costItems.length ?? 0) > 0) ||
    (form.bbmTouched && form.planInfo?.bbmFixOverride != null));

/** Payee switch: subcon drops untouched plan costs, driver gets them back (R3). */
export const setPayee = (
  form: UjpFormState,
  payee: UjpFormState['payee']
): UjpFormState => syncPlanCosts({ ...form, payee });

/**
 * A manually built or edited route no longer traces to the plan: the link,
 * note and freshness line go; the numbers stay where ops can see them.
 */
export const detachRoutePlan = (): Partial<UjpFormState> => ({
  routePlanId: '',
  planInfo: null,
  costsFromPlan: false,
});

/**
 * A picked route plan replaces the draft and re-defaults the proposed km (CR-2)
 * under the per-stop billing rule with the client rule as default (CR8-D4),
 * and pre-fills the Biaya step from the plan's costs (R1–R3).
 * Typed structurally so both a `RoutePlan` and a lighter fixture satisfy it.
 */
export const applySavedRoute = (
  form: UjpFormState,
  plan: {
    id: string;
    name?: string;
    costItems?: RoutePlanCostItem[];
    bbmFixOverride?: string | null;
    notes?: string | null;
    updatedAt?: string;
    updatedBy?: string | null;
  } & Parameters<typeof fromSavedRoute>[0],
  rule: UjpClientRule | null
): UjpFormState => {
  const draft = fromSavedRoute(plan);
  return syncPlanCosts({
    ...form,
    routePlanId: plan.id,
    route: draft,
    kmTouched: false,
    kmProposed: String(defaultKmProposed(draft, !!rule?.chargedPositioning)),
    planInfo: {
      name: plan.name ?? '',
      costItems: plan.costItems ?? [],
      bbmFixOverride: plan.bbmFixOverride ?? null,
      notes: plan.notes ?? null,
      updatedAt: plan.updatedAt ?? null,
      updatedBy: plan.updatedBy ?? null,
    },
  });
};

/**
 * The reverse switch is pre-ticked when the route ends in a pool DROP_OFF
 * (the truck goes back to the pool, CR6-D3 — was: a RETURN stop) AND the
 * client actually charges for reverse (CR-D8) — never after ops touched it.
 */
export const autoReverse = (
  form: UjpFormState,
  rule: UjpClientRule | null
): boolean => {
  if (form.reverseTouched) return form.isReverse;
  return endsInPoolDrop(form.route.stops) && toNum(rule?.reverseCharge) > 0;
};

// ─────────────────────────────────────────────────────────────────────────────
// Validation
// ─────────────────────────────────────────────────────────────────────────────

const V = UJP_COPY.validation;

export type UjpErrors = Record<string, string>;

export const validateInfo = (
  form: UjpFormState,
  now: Date = new Date()
): UjpErrors => {
  const errors: UjpErrors = {};
  if (!form.clientId) errors.clientId = V.client;
  if (!form.deliveryDate) errors.deliveryDate = V.deliveryDate;
  else if (form.deliveryDate < todayIso(now))
    errors.deliveryDate = V.deliveryDatePast;
  return errors;
};

export const validateRoute = (form: UjpFormState): UjpErrors => {
  const errors: UjpErrors = { ...routeDraftErrors(form.route) };
  const km = toNum(form.kmProposed);
  if (km <= 0) {
    errors.kmProposed = V.km;
  } else {
    // KM diajukan maksimum 10% di atas jarak rute (Σ semua leg) — guard supaya
    // km yang ditagih tidak jauh di atas jarak yang benar-benar ditempuh.
    const routeKm = kmAllLegs(form.route);
    if (routeKm > 0 && km > routeKm * 1.1) errors.kmProposed = V.kmMax(routeKm);
  }
  return errors;
};

export const validateCost = (form: UjpFormState): UjpErrors => {
  const errors: UjpErrors = {};
  if (form.payee === 'subcon') {
    // Subcon has no driver of ours (the server puts it on the placeholder
    // "Driver Subcon" account), so Biaya carries only the vendor rekening.
    // Plate is OPTIONAL free text (Direct4W snapshots a null plate cleanly);
    // unit type is REQUIRED (it drives the shipment + tariff body match).
    if (!form.vendorId) errors.vendorId = V.vendor;
    if (toNum(form.nominalTransfer) <= 0)
      errors.nominalTransfer = V.nominalTransfer;
    if (!form.unitType.trim()) errors.unitType = V.unitType;
    if (
      !form.bankName.trim() ||
      !form.accountNumber.trim() ||
      !form.accountHolder.trim()
    )
      errors.bank = V.bank;
    return errors;
  }
  // Driver payee: plate required (EV energy autofill keys on it).
  if (!form.plateNumber.trim()) errors.plateNumber = V.plate;
  if (form.energyType === 'EV') {
    if (toNum(form.konsumsiPerKm) <= 0) errors.konsumsiPerKm = V.konsumsi;
  } else if (toNum(form.baseline) <= 0) errors.baseline = V.baseline;
  if (toNum(form.energyPrice) <= 0) errors.energyPrice = V.energyPrice;
  return errors;
};

export const validateDriver = (form: UjpFormState): UjpErrors => {
  const errors: UjpErrors = {};
  // Subcon has no Driver step — its rider + rekening are validated in Biaya.
  if (form.payee !== 'driver') return errors;
  if (!form.rider.id) errors.rider = V.rider;
  if (
    !form.bankName.trim() ||
    !form.accountNumber.trim() ||
    !form.accountHolder.trim()
  )
    errors.bank = V.bank;
  return errors;
};

/** The wizard steps, in order, for a given payee — subcon skips the Driver step. */
export type UjpStepKind = 'info' | 'route' | 'cost' | 'driver' | 'review';

const DRIVER_STEP_KINDS: UjpStepKind[] = [
  'info',
  'route',
  'cost',
  'driver',
  'review',
];
const SUBCON_STEP_KINDS: UjpStepKind[] = ['info', 'route', 'cost', 'review'];

export const stepKindsFor = (payee: UjpPayeeType): UjpStepKind[] =>
  payee === 'subcon' ? SUBCON_STEP_KINDS : DRIVER_STEP_KINDS;

/** The `StepIndicator` rows for a payee — subcon has 4 steps, driver 5. */
export const wizardStepsFor = (
  payee: UjpPayeeType
): { id: number; label: string }[] =>
  stepKindsFor(payee).map((k, i) => ({ id: i + 1, label: STEP_LABEL[k] }));

const STEP_LABEL: Record<UjpStepKind, string> = {
  info: UJP_COPY.wizard.steps[0],
  route: UJP_COPY.wizard.steps[1],
  cost: UJP_COPY.wizard.steps[2],
  driver: UJP_COPY.wizard.steps[3],
  review: UJP_COPY.wizard.steps[4],
};

export const validateStepKind = (
  kind: UjpStepKind,
  form: UjpFormState,
  now: Date = new Date()
): UjpErrors => {
  switch (kind) {
    case 'info':
      return validateInfo(form, now);
    case 'route':
      return validateRoute(form);
    case 'cost':
      return validateCost(form);
    case 'driver':
      return validateDriver(form);
    default:
      return {};
  }
};

/** Every step's errors merged — the Review step's "Kelengkapan" line. */
export const validateAll = (
  form: UjpFormState,
  now: Date = new Date()
): UjpErrors => ({
  ...validateInfo(form, now),
  ...validateRoute(form),
  ...validateCost(form),
  ...validateDriver(form),
});

const KIND_OF_FIELD: Record<string, UjpStepKind> = {
  clientId: 'info',
  deliveryDate: 'info',
  kmProposed: 'route',
  route: 'route',
  plateNumber: 'cost',
  vendorId: 'cost',
  nominalTransfer: 'cost',
  unitType: 'cost',
  baseline: 'cost',
  konsumsiPerKm: 'cost',
  energyPrice: 'cost',
  rider: 'driver',
  bank: 'driver',
};

/** Which step labels are still incomplete, in step order (DD7). */
export const incompleteSteps = (
  errors: UjpErrors,
  payee: UjpPayeeType = 'driver'
): string[] => {
  const kinds = stepKindsFor(payee);
  const present = new Set<UjpStepKind>();
  Object.keys(errors).forEach((key) => {
    // Route-row errors are keyed `rstop-3.name` / `a>b.km`; cost rows `cost-….label`.
    let kind =
      KIND_OF_FIELD[key] ?? (key.startsWith('cost-') ? 'cost' : 'route');
    // Subcon has no Driver step — its rider + rekening surface under Biaya.
    if (payee === 'subcon' && kind === 'driver') kind = 'cost';
    present.add(kind);
  });
  return kinds.filter((k) => present.has(k)).map((k) => STEP_LABEL[k]);
};

// ─────────────────────────────────────────────────────────────────────────────
// Payloads
// ─────────────────────────────────────────────────────────────────────────────

/** Is there enough on the form for the server to compute anything at all? */
export const isEstimatable = (form: UjpFormState): boolean => {
  if (!form.clientId || !form.deliveryDate) return false;
  if (form.route.legs.length === 0) return false;
  if (toNum(form.kmProposed) <= 0) return false;
  if (form.payee === 'subcon') return toNum(form.nominalTransfer) > 0;
  const consumption =
    form.energyType === 'EV'
      ? toNum(form.konsumsiPerKm)
      : toNum(form.baseline);
  return consumption > 0 && toNum(form.energyPrice) > 0;
};

const costsPayload = (form: UjpFormState) => ({
  kmYangDiajukan: form.kmProposed || '0',
  bbmFixOverride: form.overrideBbm ? form.bbmFixOverride || '0' : undefined,
  // A subcon is paid one flat transfer; its (disabled) rows are not sent.
  costItems: form.payee === 'subcon' ? [] : toCostItemsPayload(form.costItems),
});

const vehiclePayload = (form: UjpFormState) => ({
  energyType: form.energyType,
  ...(form.fuelType ? { fuelType: form.fuelType } : {}),
  ...(form.energyType === 'EV'
    ? { konsumsiPerKm: form.konsumsiPerKm || '0' }
    : { baseline: form.baseline || '0' }),
  energyPrice: form.energyPrice || '0',
  // A subcon is paid one flat transfer and has no e-money field.
  eMoney: form.payee === 'subcon' ? ('NONE' as UjpEMoney) : form.eMoney,
});

/**
 * `POST /v1/ujp/estimate`. Returns null when the form cannot produce a number
 * yet, which is what puts `useUjpEstimate` into its idle state.
 */
export const toEstimatePayload = (
  form: UjpFormState
): UjpEstimatePayload | null => {
  if (!isEstimatable(form)) return null;
  return {
    header: {
      clientId: Number(form.clientId),
      deliveryDate: form.deliveryDate,
      isReverse: form.isReverse,
    },
    payee: {
      type: form.payee,
      ...(form.payee === 'subcon'
        ? { nominalTransfer: form.nominalTransfer || '0' }
        : {}),
    },
    vehicle: vehiclePayload(form),
    costs: costsPayload(form),
    route: toRoutePayload(form.route),
  };
};

/** `POST /v1/ujp`. The server recomputes every total; nothing here is a total. */
export const toCreatePayload = (form: UjpFormState): CreateUjpPayload => {
  const route = toRoutePayload(form.route);
  return {
    header: {
      clientId: Number(form.clientId),
      deliveryDate: form.deliveryDate,
      isReverse: form.isReverse,
      deliveryType: form.deliveryType,
      ...(form.serviceType ? { serviceType: form.serviceType } : {}),
      ...(form.shift ? { shift: form.shift } : {}),
      ...(form.region.trim() ? { region: form.region.trim() } : {}),
      ...(form.tariffConfigId ? { tariffConfigId: form.tariffConfigId } : {}),
      ...(form.ringId ? { ringId: form.ringId } : {}),
    },
    payee: {
      type: form.payee,
      ...(form.payee === 'subcon'
        ? {
            nominalTransfer: form.nominalTransfer || '0',
            subconVendorId: form.vendorId,
          }
        : {}),
      bankName: form.bankName.trim(),
      accountNumber: form.accountNumber.trim(),
      accountHolder: form.accountHolder.trim(),
    },
    vehicle: {
      ...vehiclePayload(form),
      plateNumber: form.plateNumber.trim(),
      ...(form.unitType ? { unitType: form.unitType.trim() } : {}),
      ...(form.payee !== 'subcon' && form.eMoneyCardNo.trim()
        ? { eMoneyCardNo: form.eMoneyCardNo.trim() }
        : {}),
      ...(form.vehicleId ? { vehicleId: form.vehicleId } : {}),
    },
    costs: {
      ...costsPayload(form),
      ...(form.justifikasi.trim()
        ? { justifikasiBiayaLainLain: form.justifikasi.trim() }
        : {}),
    },
    route: {
      ...route,
      ...(form.routePlanId ? { routePlanId: form.routePlanId } : {}),
    },
    // Only a driver payee names its driver; the server puts a subcon on the
    // placeholder "Driver Subcon" account.
    ...(form.payee === 'driver' && form.rider.id
      ? {
          rider: {
            id: form.rider.id,
            code: form.rider.code,
            name: form.rider.name.trim(),
            phoneNumber: form.rider.phone.trim(),
          },
        }
      : {}),
  };
};

// ─────────────────────────────────────────────────────────────────────────────
// "Buat ulang dari UJP ini"
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Rebuild a form from a rejected UJP (DD6). Everything the requester typed
 * comes back; the reference id, the saved-route flag and the delivery date do
 * not — the number is the server's and the old date may be in the past.
 */
export const formFromDetail = (
  detail: UjpDetail,
  now: Date = new Date()
): UjpFormState => {
  const base = blankUjpForm(now);
  const draft = fromSavedRoute({
    stops: detail.route.stops,
    legs: detail.route.legs,
  });
  const past = detail.header.deliveryDate < todayIso(now);
  return {
    ...base,
    clientId: String(detail.header.clientId ?? detail.client?.id ?? ''),
    deliveryDate: past ? todayIso(now) : detail.header.deliveryDate,
    serviceType: detail.header.serviceType ?? '',
    deliveryType: detail.header.deliveryType ?? 'SINGLE_DROP',
    shift: detail.header.shift ?? '',
    region: detail.header.region ?? '',
    isReverse: !!detail.header.isReverse,
    reverseTouched: true,

    tariffConfigId: detail.header.tariffConfigId ?? '',
    ringId: detail.header.ringId ?? '',
    ringTouched: !!detail.header.ringId,

    routePlanId: detail.route.routePlanId ?? '',
    // A rejected UJP's numbers are ops' own: a plan pick must not overwrite them (R1).
    costsTouched: true,
    route: draft,
    kmProposed: String(toNum(detail.costs.kmYangDiajukan) || ''),
    kmTouched: true,

    payee: detail.payee.type,
    vendorId: detail.payee.subconVendorId ?? '',
    nominalTransfer: moneyDigits(detail.payee.nominalTransfer),
    plateNumber: detail.vehicle.plateNumber ?? '',
    unitType: detail.vehicle.unitType ?? '',
    vehicleId: detail.vehicle.vehicleId ?? '',
    energyType: detail.vehicle.energyType,
    fuelType: detail.vehicle.fuelType ?? '',
    baseline: detail.vehicle.baseline != null ? String(detail.vehicle.baseline) : '',
    konsumsiPerKm:
      detail.vehicle.konsumsiPerKm != null
        ? String(detail.vehicle.konsumsiPerKm)
        : '',
    baselineTouched: true,
    energyPrice: moneyDigits(detail.vehicle.energyPrice),
    priceTouched: true,
    eMoney: detail.vehicle.eMoney,
    eMoneyCardNo: detail.vehicle.eMoneyCardNo ?? '',
    overrideBbm: detail.costs.bbmFixOverride != null,
    bbmFixOverride: moneyDigits(detail.costs.bbmFixOverride),
    // A rejected UJP's override is ops' own too: a plan pick must not flip it.
    bbmTouched: true,
    costItems: toCostDrafts(detail.costs.costItems ?? []),
    justifikasi: foldedCostNote(
      detail.costs.justifikasiBiayaLainLain ?? '',
      detail.costs.costItems ?? []
    ),

    rider: detail.rider
      ? {
          id: String(detail.rider.id),
          code: '',
          name: detail.rider.name,
          phone: '',
        }
      : emptyRider(),
    bankName: detail.payee.bankName ?? '',
    // A masked account ("****1234") is not a number — make the requester retype.
    accountNumber: /^\d+$/.test(detail.payee.accountNumber ?? '')
      ? detail.payee.accountNumber
      : '',
    accountHolder: detail.payee.accountHolder ?? '',
  };
};

/** `"185000.00"` → `"185000"` — MoneyInput speaks whole-rupiah digits. */
export const moneyDigits = (value: string | number | null | undefined): string =>
  value == null || value === '' ? '0' : String(Math.round(toNum(value)));

// ─────────────────────────────────────────────────────────────────────────────
// Read-only derivations for the step bodies
// ─────────────────────────────────────────────────────────────────────────────

/**
 * The Rute step / Review km line (CR8-D4): `charged` follows the per-stop rule
 * with the client rule as default, `anyPoolCharged` mirrors the estimate's
 * `chargedPositioning`, and `override` is the LOCAL pre-estimate hint for the
 * "Beda dari aturan client" tag (the server flag wins once an estimate lands).
 */
export const kmSummary = (form: UjpFormState, rule: UjpClientRule | null) => {
  const clientDefault = !!rule?.chargedPositioning;
  return {
    all: kmAllLegs(form.route),
    charged: kmCharged(form.route, clientDefault),
    anyPoolCharged: routeAnyPoolCharged(form.route, clientDefault),
    override: routeChargedOverride(form.route, clientDefault),
  };
};
